import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MODULE_REGISTRY } from '../../modules/registry';
import { userHasAnyMenuIn, RbacUser } from '../../auth/menuHelpers';
import { useAuth } from '../../contexts/AuthContext';
import { MODULE_META } from './moduleMeta';
import './AppShell.css';

export interface HomeModuleListProps {
  /** Sidebar is in 56px tablet icon-rail mode. */
  collapsed?: boolean;
  /** Click handler so the phone drawer can close after navigating. */
  onNavigate?: () => void;
}

/**
 * Direct module-navigation surface rendered in the sidebar when no module is
 * active (Home and any other non-module URL). Replaces the two-click flow
 * (open switcher → pick module) with one click per module.
 *
 * Disabled modules (Fertilizing, Picking) render as inert rows with a "Coming
 * Soon" badge, matching the popover ModuleSwitcher's treatment.
 *
 * Visibility rules (from MODULE_REGISTRY + userHasAnyMenuIn):
 * - Superadmin: only superadminOnly modules shown.
 * - Tenant users: all non-superadminOnly modules where the user has any menu
 *   OR the module is comingSoon (always rendered for tenant users as disabled).
 */
const HomeModuleList: React.FC<HomeModuleListProps> = ({ collapsed, onNavigate }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.language === 'af' ? 'af' : 'en') as 'en' | 'af';

  // Step 1: filter to modules appropriate for this user type
  const modules = Object.values(MODULE_REGISTRY).filter(mod => {
    if (user?.superadmin) return mod.superadminOnly;
    return !mod.superadminOnly;
  });

  return (
    <nav className="shell-sidebar__nav" aria-label={t('shell.modulesLabel')} data-testid="home-module-list">
      {modules.map(mod => {
        const hasAccess = userHasAnyMenuIn(user as RbacUser | null, mod.key);
        // comingSoon tiles are always visible for tenant users; other tiles require access
        const visible = hasAccess || mod.comingSoon;
        if (!visible) return null;

        const isDisabled = mod.comingSoon || !hasAccess;
        // Land on the module overview, not on a specific menu — clicking a
        // module tile should always go to the module root. /settings and
        // /system have index redirects to their primary sub-route.
        const moduleRoot = MODULE_META.find(m => m.id === mod.key)?.to;
        const to = moduleRoot || mod.menus[0].path;

        if (isDisabled) {
          return (
            <div
              key={mod.key}
              className="shell-sidebar-item shell-sidebar-item--disabled"
              aria-disabled="true"
              data-testid={`home-module-${mod.key}`}
            >
              <i className={`bi ${mod.icon}`} data-testid="sidebar-item-icon" aria-hidden="true" />
              {!collapsed && (
                <span className="shell-sidebar-item__label">
                  {mod.label[lang]}
                  <span className="shell-sidebar-item__badge">{t('home.modules.comingSoon')}</span>
                </span>
              )}
            </div>
          );
        }

        return (
          <NavLink
            key={mod.key}
            to={to}
            onClick={onNavigate}
            className="shell-sidebar-item"
            data-testid={`home-module-${mod.key}`}
          >
            <i className={`bi ${mod.icon}`} data-testid="sidebar-item-icon" aria-hidden="true" />
            {!collapsed && <span className="shell-sidebar-item__label">{mod.label[lang]}</span>}
          </NavLink>
        );
      })}
    </nav>
  );
};

export default HomeModuleList;
